/**
 * Interactive Commercial Request System
 *
 * This system allows potential clients to configure their development needs
 * by selecting from various development sections and technology options.
 * The client receives a customized proposal based on their selections.
 */

/**
 * Represents a development area/category that clients can choose from.
 * Examples: "Frontend", "Backend", "Database", "Hosting", etc.
 */
export interface Section {
    /** Unique identifier for the section */
    id: string;
    /** Display title for the development section (e.g., "Frontend Development", "Backend Development") */
    title: LocalizedString;
    /** Whether this section is optional for the project */
    optional: boolean;
    /** Number of options that can be selected from this section. If undefined, unlimited selections are allowed */
    options_num?: number;
    /** The mode this section belongs to (e.g., "3d", "2d"). If undefined, visible in all modes */
    visible_on_mode?: string[];
}

export enum Locale {
    ru = 'ru',
    en = 'en',
    kz = 'kz'
}

/**
 * Helper type for localized string content
 */
export type LocalizedString = Record<Locale, string>;

/**
 * Helper type for localized string array content (e.g., pros/cons lists)
 */
export type LocalizedStringArray = Record<Locale, string[]>;

export enum Currency {
    USD = 'us',
    EUR = 'eu',
    KZT = 'kzt',
    RUB = 'rub'
}

/**
 * Represents a project mode (e.g., 2D vs 3D)
 */
export interface Mode {
    id: string;
    title: LocalizedString;
}

/**
 * Represents a specific technology or approach within a development section.
 * Examples: For Backend section - "Node.js", "Python", "Java Spring Boot"
 *           For Frontend section - "React", "Vue", "Angular"
 */
export interface Option {
    /** Unique identifier for the option */
    id: string;
    /** Reference to the parent section */
    section_id: string;
    /** Technology or method name (e.g., "Node.js", "React.js", "PostgreSQL") */
    title: LocalizedString;
    /** Detailed explanation of this technology choice */
    description: LocalizedString;
    /** Cost associated with choosing this option (optional) */
    price?: {
        [Currency.USD]: number;
        [Currency.EUR]: number;
        [Currency.KZT]: number;
        [Currency.RUB]: number;
    };
    /** Benefits and advantages of this technology choice */
    pros?: LocalizedStringArray;
    /** Potential drawbacks or limitations */
    cons?: LocalizedStringArray;
    /** List of subtasks or components included in this option (displayed as badges) */
    subtasks?: LocalizedStringArray;
    /** Estimated number of days required to complete this option */
    days_till_completed?: number;
    /** Optional image to display with this option */
    image?: {
        url: string;
        title: LocalizedString;
    };
    /** Whether this option is priced per hour (hourly rate) instead of fixed price */
    is_per_hour?: boolean;
    /** Whether this option is currently selected by the client */
    is_selected?: boolean;
    /** Whether this option can be selected multiple times (e.g., multiple design variants) */
    allow_multiple_instances?: boolean;
    /** Maximum number of instances that can be selected (if allow_multiple_instances is true) */
    max_instances?: number;
    /** Minimum number of instances required (if allow_multiple_instances is true, default: 1) */
    min_instances?: number;
    /** ID of another option that must be selected for this option to be available */
    depends_on?: string;
    /** Visual style of the option: 'card' (default) or 'switch' */
    ui_type?: 'card' | 'switch';
}

/**
 * Represents a default option that is always included in the project
 */
export interface DefaultOption {
    /** Unique identifier for the default option */
    id: string;
    /** Localized name of the default option */
    title: LocalizedString;
    /** Localized description of what's included */
    description?: LocalizedString;
    /** List of subtasks or components included in this default option */
    subtasks?: LocalizedStringArray;
    /** Cost associated with this default option (optional) */
    price?: {
        [Currency.USD]: number;
        [Currency.EUR]: number;
        [Currency.KZT]: number;
        [Currency.RUB]: number;
    };
    /** Estimated number of days for this default option */
    days_till_completed?: number;
    /** List of modes where this default option is allowed (e.g. ["3d", "2d"]). If undefined, allowed in all modes */
    allowed_modes?: string[];
}

/**
 * Represents the data structure from data.json
 */
export interface ProjectData {
    companyName?: string;
    modesLabel?: LocalizedString;
    modes?: Mode[];
    sections: Section[];
    options: Option[];
    defaultOptions?: DefaultOption[];
}

/**
 * Raw JSON Interfaces - These accurately represent the data.json file structure
 */

/**
 * Section as it appears in data.json
 */
export interface DataJsonSection {
    /** Unique identifier for the section */
    id: string;
    /** Localized display title for the development section */
    title: LocalizedString;
    /** Whether this section is optional for the project */
    optional: boolean;
    /** Number of options that can be selected from this section. If undefined, unlimited selections are allowed */
    options_num?: number;
    /** The mode this section belongs to (e.g., "3d", "2d"). If undefined, visible in all modes */
    visible_on_mode?: string[];
}

/**
 * Option as it appears in data.json
 */
export interface DataJsonOption {
    /** Unique identifier for the option */
    id: string;
    /** Reference to the parent section */
    section_id: string;
    /** Localized technology or method name */
    title: LocalizedString;
    /** Localized detailed explanation of this technology choice */
    description: LocalizedString;
    /** Cost associated with choosing this option in multiple currencies (optional) */
    price?: {
        [Currency.USD]: number;
        [Currency.EUR]: number;
        [Currency.KZT]: number;
        [Currency.RUB]: number;
    };
    /** Localized benefits and advantages of this technology choice */
    pros?: LocalizedStringArray;
    /** Localized potential drawbacks or limitations */
    cons?: LocalizedStringArray;
    /** List of subtasks or components included in this option (displayed as badges) */
    subtasks?: LocalizedStringArray;
    /** Estimated number of days required to complete this option */
    days_till_completed?: number;
    /** Whether this option is priced per hour (hourly rate) instead of fixed price */
    is_per_hour?: boolean;
    image?: {
        url: string;
        title: LocalizedString;
    };
    /** Whether this option can be selected multiple times (e.g., multiple design variants) */
    allow_multiple_instances?: boolean;
    /** Maximum number of instances that can be selected (if allow_multiple_instances is true) */
    max_instances?: number;
    /** Minimum number of instances required (if allow_multiple_instances is true, default: 1) */
    min_instances?: number;
    /** ID of another option that must be selected for this option to be available */
    depends_on?: string;
    /** Visual style of the option: 'card' (default) or 'switch' */
    ui_type?: 'card' | 'switch';
}

export interface ProjectDescriptionConfig {
    /** Title of the project description section */
    title: LocalizedString;
    /** The actual description text to display */
    description: LocalizedString;
    /** List of image URLs to display */
    images: string[];
}

/**
 * Root structure of data.json file
 */
export interface DataJson {
    /** Company name */
    company_name: string;
    /** Label for the modes section */
    modes_label?: LocalizedString;
    /** Array of available modes */
    modes?: Mode[];
    /** Project description configuration */
    project_description?: ProjectDescriptionConfig;
    /** Array of development sections */
    sections: DataJsonSection[];
    /** Array of technology options */
    options: DataJsonOption[];
    /** Default options that are always included */
    default_options?: DefaultOption[];
}

/**
 * Represents the data structure from data.json
 */
export interface ProjectData {
    companyName?: string;
    modesLabel?: LocalizedString;
    modes?: Mode[];
    projectDescriptionConfig?: ProjectDescriptionConfig;
    sections: Section[];
    options: Option[];
    defaultOptions?: DefaultOption[];
}

/**
 * Represents a selected option with its quantity
 */
export interface SelectedOption {
    option: Option;
    quantity: number;
}

/**
 * Represents the current selection state
 */
export interface SelectionState {
    selectedOptions: Map<string, Option[]>; // section_id -> selected options
    totalPrice: number;
    projectDeadline: number; // days
}

/**
 * Question interfaces for client questionnaire
 */

/**
 * Represents a question that requires text input from the client
 */
export interface Question {
    /** Unique identifier for the question */
    id: string;
    /** The question text in multiple languages */
    question: LocalizedString;
    /** Placeholder text for the input field */
    placeholder?: LocalizedString;
    /** Whether this question requires an answer */
    required: boolean;
    /** Type of input field to display */
    inputType: 'text' | 'textarea' | 'email' | 'phone';
}

/**
 * Represents a client's response to a question
 */
export interface QuestionResponse {
    /** Reference to the question ID */
    questionId: string;
    /** The client's answer */
    answer: string;
}

/**
 * Structure of the questions.json file
 */
export interface QuestionsData {
    questions: Question[];
}